import type { DependencyList } from "react"
import { useEffect, useRef, useState } from "react"
import Mention from "@tiptap/extension-mention"
import Placeholder from "@tiptap/extension-placeholder"
import { Editor } from "@tiptap/react"

import type { typographyModeType } from "@productlane/ui"
import { cn, getDefaultExtensions } from "@productlane/lib/src/browser"
import { typographyVariants } from "@productlane/ui"

import type { EditorOptions } from "@tiptap/react"
import suggestions from "@/screens/app/threads/comments/suggestions"
import { DEFAULT_EDITOR_PROPS } from "./editor-config"
import { EmojiSuggestion } from "./extensions/emoji"
import { insertMarkdownPaste } from "./markdown-paste"

interface Props {
	editable?: boolean
	initialContent?: string
	mode?: typographyModeType
}

const markdownPasteExtensions = getDefaultExtensions({ image: false })

export default function useCommentEditor({
	initialContent,
	editable,
	mode,
}: Props) {
	const editorRef = useRef<Editor | null>(null)
	const editor = useCustomEditor(
		{
			editable,
			content: initialContent,
			editorProps: {
				...DEFAULT_EDITOR_PROPS,
				handlePaste: (view, event) =>
					insertMarkdownPaste({
						view,
						event,
						editor: editorRef.current,
						extensions: markdownPasteExtensions,
					}),
				attributes: {
					...DEFAULT_EDITOR_PROPS.attributes,
					class: cn(
						typographyVariants({ variant: "message", mode }),
						"text-sm focus:outline-none focus:ring-0 focus:border-transparent dark:text-gray-50",
					),
				},
			},
			extensions: [
				...getDefaultExtensions({
					image: false,
				}),
				EmojiSuggestion,
				Mention.configure({
					renderLabel: ({ node, options }) => {
						return `${options.suggestion.char}${
							node.attrs.label ?? node.attrs.id
						}`
					},
					HTMLAttributes: {
						class: "font-semibold",
					},
					suggestion: suggestions,
				}),
				Placeholder.configure({
					placeholder: "Write a comment...",
				}),
			],
		},
		[],
	)

	editorRef.current = editor
	return editor
}

export function createCommentEditorInstance({
	initialContent = "",
	mode,
	placeholder = "Write a comment...",
	autofocus = "end",
}: {
	initialContent?: string
	mode?: typographyModeType
	placeholder?: string
	autofocus?: "end" | false
} = {}): Editor {
	let instance: Editor | null = null
	instance = new Editor({
		content: initialContent,
		editable: true,
		autofocus,
		editorProps: {
			...DEFAULT_EDITOR_PROPS,
			handlePaste: (view, event) =>
				insertMarkdownPaste({
					view,
					event,
					editor: instance,
					extensions: markdownPasteExtensions,
				}),
			attributes: {
				...DEFAULT_EDITOR_PROPS.attributes,
				class: cn(
					typographyVariants({ variant: "message", mode }),
					"text-sm focus:outline-none focus:ring-0 focus:border-transparent dark:text-gray-50",
				),
			},
		},
		extensions: [
			...getDefaultExtensions({ image: false }),
			EmojiSuggestion,
			Mention.configure({
				renderLabel: ({ node, options }) =>
					`${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`,
				HTMLAttributes: { class: "font-semibold" },
				suggestion: suggestions,
			}),
			Placeholder.configure({ placeholder }),
		],
	})
	return instance
}

function useForceUpdate() {
	const [, setValue] = useState(0)
	return () => setValue((value) => value + 1)
}

// avoid flickering by using a custom useEditor hook https://gist.github.com/ryanto/4a431d822a98770c4ca7905d9b7b07da
export const useCustomEditor = (
	options: Partial<EditorOptions> = {},
	deps: DependencyList = [],
) => {
	const [editor, setEditor] = useState<Editor>(() => new Editor(options))
	const forceUpdate = useForceUpdate()

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		let instance: Editor

		if (editor.isDestroyed) {
			instance = new Editor(options)
			setEditor(instance)
		} else {
			instance = editor
		}

		instance.on("transaction", () => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					forceUpdate()
				})
			})
		})

		return () => {
			instance.destroy()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps)

	return editor
}
